import {
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EpikPaymentMethod } from '@fmnts/api';
import { Campaign } from '@fmnts/api/next';
import { ConnectionService, hasConnectionStatus } from '@fmnts/common';
import { TranslationService, languageFromLocale } from '@fmnts/i18n';
import { LegacyStore } from '@formunauts/next/data-access';
import { RaiseNowSettings } from '@formunauts/shared/vendors/raisenow/data-access';
import { faCloud, faWifiSlash } from '@fortawesome/pro-regular-svg-icons';
import {
  faCheck,
  faCreditCardFront,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import {
  CreditCardIframe,
  Epik,
  EpikConfig,
  Payment,
  PaymentConfig,
  PaymentFlow,
  PaymentMethod,
  PaymentProvider,
  TransactionInfo,
  TransactionStatus,
} from '@raisenow/epik';
import { isBoolean } from 'effect/Boolean';
import { ReadonlyRecord } from 'effect/Record';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  concat,
  concatMap,
  defer,
  delay,
  exhaustMap,
  filter,
  finalize,
  first,
  from,
  fromEvent,
  map,
  of,
  partition,
  race,
  retryWhen,
  skip,
  startWith,
  switchMap,
  switchMapTo,
  take,
  takeUntil,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { fromCreditCardIframeMessage } from '../../rxjs/from-credit-card-iframe-message';
import {
  PaymentStatus,
  RaisenowPaymentService,
} from '../../services/raisenow-payment.service';
import { RaiseNowModalService } from '../raisenow-modal/raisenow-modal.service';
import {
  RaiseNowExtraFields,
  RaiseNowUtilService,
} from './raisenow-util.service';
import { RaiseNowCreditCardUpdateTranslationsPayload } from './raisenow.types';
import { prepareAdditionalRaiseNowCreditCardTranslations } from './translations/raisenow-cc.l10n';

/**
 * Legacy EPP payment iframe that supports RaiseNow's older workflow (Europe).
 * Their newer workflow is supported by the RaiseNowIframeComponent (PayU).
 */
@Component({
  selector: 'next-raisenow-iframe-epp',
  templateUrl: './raisenow-iframe-epp.component.html',
  styleUrls: ['./raisenow-iframe-epp.component.scss'],
  providers: [RaisenowPaymentService],
})
export class RaiseNowIframeEPPComponent implements OnInit, OnDestroy {
  private readonly rnwSettings = inject(RaiseNowSettings);

  @ViewChild('raiseNowIframeEPP', { static: false })
  raiseNowIframeEPP: ElementRef<HTMLDivElement>;

  /**
   * Emits the raisenow transaction info on success
   */
  @Output() paymentResult = new EventEmitter();

  /**
   * Emits when the raisenow modal is closed
   */
  @Output() closed = new EventEmitter<any>();

  /**
   * Emits when the "Cancel" button was clicked
   */
  private readonly _cancelPayment$ = new Subject<void>();

  /**
   * Emits when the "Pay" button was clicked
   */
  private readonly _sendPayment$ = new Subject<void>();

  /**
   * Container subscription for everything set up
   * in the constructor or ngOnInit
   */
  private readonly subscription = new Subscription();
  /**
   * `true` if the service is unavailable.
   * Could be that some server error occured while setting up
   * or processing a payment request.
   */
  public serviceUnavailable = false;
  public hasPaymentStarted = false;
  public isIndicatorLoading = true;
  public isMpos: boolean;
  public isPaymentButtonDisabled = false;
  public offline = false;
  public responseErrorParagraph;
  public responseErrorHeading;
  public responseError = false;
  public showButtonLoadingIndicator = false;
  public showIframe = false;

  public PaymentStatus = PaymentStatus;
  public paymentStatus: PaymentStatus;

  /**
   * `Observable` that emits with the payment
   */
  private payment$ = this.rnPaymentService.payment$;
  /**
   * `Observable` that emits with the payment status
   */
  private paymentStatus$ = this.rnPaymentService.store$.pipe(
    map(({ state }) => state),
  );

  // Icons
  protected readonly _iconMpos = faCreditCardFront;
  /** @internal */
  protected readonly _iconWarning = faExclamationTriangle;
  /** @internal */
  protected readonly _iconError = faExclamationTriangle;
  /** @internal */
  protected readonly _iconOffline = faWifiSlash;
  /** @internal */
  protected readonly _iconServiceUnavailable = faCloud;
  /** @internal */
  protected readonly _iconStartPayment = faCheck;

  /** Delay before enabling the cancel button */
  private readonly cancelableDelay = 30 * 1000;

  private readonly isOffline$ = this.connectionService.connectionStatus$.pipe(
    hasConnectionStatus('offline'),
  );

  /**
   * Observable that emits with a boolean value that is `true` when
   * the cancel button should be clickable.
   */
  public readonly _canCancel$ = this.paymentStatus$.pipe(
    switchMap((status) => {
      // Some states should delay the cancel option
      if (status === PaymentStatus.Sent) {
        return of(true).pipe(delay(this.cancelableDelay), startWith(false));
      }
      // Other cases that emit immediately
      const hideCancelButton = [PaymentStatus.Sending].includes(status);
      return of(!hideCancelButton);
    }),
  );

  constructor(
    private connectionService: ConnectionService,
    private ngZone: NgZone,
    private raiseNowModalService: RaiseNowModalService,
    private raiseNowUtilService: RaiseNowUtilService,
    private route: ActivatedRoute,
    private router: Router,
    private store: LegacyStore,
    private translationService: TranslationService,
    private rnPaymentService: RaisenowPaymentService,
  ) {
    const [openModal$, closeModal$] = partition(
      this.raiseNowModalService.openEPPModal,
      (isModalShown) => isModalShown,
    );

    // if there is an epp_transaction_id query param, continue process
    // if there are no query params we're starting a new EPP process from scratch
    const [continueModal$, startModal$] = partition(
      openModal$.pipe(withLatestFrom(this.route.queryParamMap)),
      ([modalOpen, queryParams]) => queryParams.has('epp_transaction_id'),
    );

    // Keep the is offline variable up to date
    this.subscription.add(
      this.isOffline$.subscribe((isOffline) => {
        this.offline = isOffline;
      }),
    );

    // Keep the language up to date
    this.subscription.add(
      this.translationService.currentLanguage$
        .pipe(map(languageFromLocale))
        .subscribe((language) =>
          this.rnPaymentService.updateLanguage(language),
        ),
    );

    // Create payment
    this.subscription.add(
      startModal$
        .pipe(switchMap(() => this.createPayment$()))
        .subscribe((payment) => {
          this.isMpos = payment.read()?.payment_method === PaymentMethod.MPOS;
        }),
    );

    // Prepare the view whenever a new payment is created
    this.subscription.add(
      this.rnPaymentService.payment$
        .pipe(
          switchMap((payment) =>
            this.prepareView$(payment).pipe(
              map(() => payment),
              takeUntil(this._cancelPayment$),
            ),
          ),
        )
        .subscribe(),
    );

    // Submit payment
    this.subscription.add(
      this._sendPayment$.pipe(exhaustMap(() => this.send())).subscribe(),
    );

    // Continue RaiseNow payment after redirect
    this.subscription.add(
      continueModal$
        .pipe(
          exhaustMap(() => this.continue()),
          takeUntil(this._cancelPayment$),
        )
        .subscribe({
          next: (transactionInfo) => {
            // Transaction info was fetched. The result of the
            // transaction depends on its state
            this.onPaymentResult(transactionInfo);
          },
          error: (e) => {
            // There was some unexpected error (e.g. server error / offline)
            // which means that the final result of the transaction is
            // unknown
            this.serviceUnavailable = true;
          },
        }),
    );

    // Close modal on cancel button click
    this.subscription.add(
      this._cancelPayment$
        .pipe(withLatestFrom(this.payment$.pipe(startWith(null as Payment))))
        .subscribe(([v, payment]) => {
          payment?.hideCreditCardIframe();
          this.closed.emit();
        }),
    );

    // Always make sure to reset the state when we close the modal.
    this.subscription.add(
      closeModal$.subscribe(() => {
        this.rnPaymentService.reset();
      }),
    );

    // Update status
    this.subscription.add(
      this.paymentStatus$.subscribe((paymentStatus) => {
        this.ngZone.run(() => {
          this.updateStatus(paymentStatus);
        });
      }),
    );

    // If we are continuing the modal execution after a redirect
    // we want to cleanup the added query params, when the modal
    // is closed.
    this.subscription.add(
      continueModal$
        .pipe(
          switchMapTo(closeModal$),
          exhaustMap(() =>
            this.router.navigate([], {
              relativeTo: this.route,
            }),
          ),
        )
        .subscribe(),
    );
  }

  ngOnInit() {
    const campaign$ = this.store
      .select<Campaign>(
        `next.campaigns.${this.route.parent.parent.snapshot.params.id}`,
      )
      .pipe(filter((campaign) => !!campaign));

    // Setup EPIK from campaign
    this.subscription.add(
      campaign$.pipe(first()).subscribe((campaign) => {
        this.rnPaymentService.setupEpik(this.createEpikConfig(campaign));
      }),
    );
  }

  ngOnDestroy(): void {
    this._cancelPayment$.complete();
    this._sendPayment$.complete();
    this.subscription.unsubscribe();
  }

  /**
   * Sends the payment
   */
  sendPayment() {
    this._sendPayment$.next();
  }

  /**
   * Cancels the payment
   */
  cancelPayment() {
    this._cancelPayment$.next();
  }

  private createPayment$() {
    return this.rnPaymentService.store$.pipe(
      first(),
      switchMap(({ epik }) => {
        const formData = this.raiseNowModalService.getFormData();
        const { raiseNowExtraFields } = this.raiseNowModalService;
        const paymentConfig = this.createPaymentConfig(
          epik,
          formData,
          raiseNowExtraFields,
        );

        return this.rnPaymentService.create(paymentConfig);
      }),
    );
  }

  /**
   * Initializes the view for the given `payment`.
   * Depending on the chosen payment method there might be
   * differences in preparing the view.
   *
   * @param payment Payment
   *
   * @returns
   * `Observable` that emits and completes when the view is initialized.
   */
  private prepareView$(payment: Payment): Observable<unknown> {
    const { payment_method } = payment.read();

    switch (payment_method) {
      // Payment methods that require the iframe to be set up
      case PaymentMethod.CREDIT_CARD:
      case PaymentMethod.STRIPE:
        return this.showCreditCardIframe$(payment).pipe(
          tap((ccIframe) => {
            // Inject additional translations for languages that are
            // not yet supported by raisenow.
            ccIframe.messenger.emit(
              'rnw.credit-card.update-translations',
              this.localizeCreditCardIframe(),
            );
          }),
          tap(() => {
            this.rnPaymentService.markAsReady();
          }),
        );
      // Payment methods that don't require the iframe to be set up
      case PaymentMethod.MPOS:
        return this.isOffline$.pipe(
          filter((isOffline) => !isOffline),
          first(),
          tap(() => {
            this.sendPayment();
          }),
        );
      default:
        return throwError(
          () => new Error(`Unsupported Payment method ${payment_method}`),
        );
    }
  }

  /**
   * @param payment EPIK Payment object
   *
   * @returns
   * `Observable` that emits when the credit card iframe was initialized
   * and then completes.
   */
  private showCreditCardIframe$(payment: Payment) {
    // Identifyable error that will be thrown if offline
    const offlineError = 'offline';
    const [offline$, online$] = partition(
      this.isOffline$,
      (isOffline) => isOffline,
    );

    // Show iframe when online or
    // throw an error if offline
    return race(
      online$.pipe(
        tap(() => (this.isIndicatorLoading = true)),
        take(1),
        switchMap(() => {
          this.serviceUnavailable = false;
          const iframeId = `#${this.raiseNowIframeEPP.nativeElement.id}`;

          return showCreditCardIframe$(payment, iframeId).pipe(
            catchError((e) => {
              this.serviceUnavailable = true;
              return throwError(e);
            }),
          );
        }),
      ),
      offline$.pipe(switchMap(() => throwError(offlineError))),
    ).pipe(
      finalize(() => {
        this.isIndicatorLoading = false;
      }),
      retryWhen((error$) =>
        error$.pipe(
          switchMap((e) =>
            offlineError === e ? online$ : online$.pipe(skip(1)),
          ),
        ),
      ),
    );
  }

  /**
   * Validates and submits the current payment.
   *
   * This will redirect to RaiseNow and back to
   * our platform, containing the created EPP
   * transaction ID.
   *
   * @returns
   * `Observable` that completes when payment was sent.
   */
  private send() {
    return this.rnPaymentService.validate().pipe(
      filter((isValid) => isValid),
      concatMap(() => this.rnPaymentService.send()),
    );
  }

  /**
   * Continues payment process after redirect.
   *
   * @returns
   * `Observable` that emits the current payment transaction info
   * and that completes when the transaction is no longer 'pending'.
   */
  private continue() {
    return this.rnPaymentService.continue().pipe(
      exhaustMap((transactionInfo) => {
        const first$ = of(transactionInfo);

        // Handle MPOS
        this.isMpos = PaymentMethod.MPOS === transactionInfo.payment_method;
        if (
          this.isMpos &&
          TransactionStatus.PENDING === transactionInfo.epayment_status
        ) {
          return concat(
            first$,
            // Make sure to be online before opening MPOS terminal web app
            this.isOffline$.pipe(
              filter((isOffline) => !isOffline),
              first(),
              switchMapTo(
                this.openMposTab$(transactionInfo.epp_transaction_id),
              ),
            ),
          );
        }

        // Default handling
        return first$;
      }),
    );
  }

  /**
   * Handle final payment result
   *
   * @param transactionInfo Transaction info for the payment
   */
  private onPaymentResult(transactionInfo: TransactionInfo) {
    switch (transactionInfo.epayment_status) {
      case TransactionStatus.CANCEL:
        this.onCanceledPayment(transactionInfo);
        break;
      case TransactionStatus.ERROR:
        this.onFailedPayment(transactionInfo);
        break;
      case TransactionStatus.SUCCESS:
        this.onSuccessfulPayment(transactionInfo);
        break;
    }
  }

  /**
   * Handle successful payment.
   *
   * @param transactionInfo
   */
  private onSuccessfulPayment(transactionInfo) {
    this.closed.emit();
    this.paymentResult.emit(transactionInfo);
  }

  /**
   * Handle failed payment.
   *
   * @param transactionInfo Transaction info if available
   */
  private onFailedPayment(transactionInfo?: TransactionInfo) {
    // if there is an error show the message and only allow cancel
    this.responseError = true;
    // Set error message
    this.responseErrorParagraph = this.translationService.getTranslation(
      'raisenow_payment_failure_paragraph',
    );
    this.responseErrorHeading = this.translationService.getTranslation(
      'raisenow_payment_failure_heading',
    );
  }

  /**
   * Handle canceled payment.
   *
   * @param transactionInfo
   */
  private onCanceledPayment(transactionInfo) {
    // Nothing to do for now
  }

  /**
   * Updates the UI for the given `paymentStatus`.
   *
   * @param paymentStatus new payment status
   */
  private updateStatus(paymentStatus: PaymentStatus) {
    this.paymentStatus = paymentStatus;

    this.showButtonLoadingIndicator = PaymentStatus.Sending === paymentStatus;
    this.isPaymentButtonDisabled = PaymentStatus.Sending === paymentStatus;

    this.hasPaymentStarted = [
      PaymentStatus.Sending,
      PaymentStatus.Sent,
    ].includes(paymentStatus);

    if (!this.isMpos) {
      this.showIframe = [PaymentStatus.Ready, PaymentStatus.Sending].includes(
        paymentStatus,
      );
    }

    this.isIndicatorLoading = [
      PaymentStatus.New,
      PaymentStatus.Created,
      PaymentStatus.Sending,
      PaymentStatus.Sent,
    ].includes(paymentStatus);

    switch (paymentStatus) {
      case PaymentStatus.New:
        this.responseErrorHeading = undefined;
        this.responseErrorParagraph = undefined;
        this.responseError = false;
        break;
    }
  }

  /**
   * @param epik
   * @param formData
   * @param raiseNowExtraFields
   *
   * @returns
   * Payment configuration from parsed form/campaign data
   */
  private createPaymentConfig(
    epik: Epik,
    formData: ReadonlyRecord<string, any>,
    raiseNowExtraFields: Readonly<RaiseNowExtraFields>,
  ): PaymentConfig {
    const language = languageFromLocale(
      this.translationService.currentLanguage,
    );

    const paymentConfig = this.raiseNowUtilService.createEpikPaymentObjectEPP(
      formData,
      raiseNowExtraFields,
      epik.config,
    ) as PaymentConfig;

    if ('payment_method' in formData) {
      let payment_method: PaymentMethod;
      let payment_provider: PaymentProvider;
      const paymentMethod: string = formData.payment_method;

      if (
        paymentMethod === EpikPaymentMethod.MposLegacy ||
        paymentMethod === EpikPaymentMethod.MposViaWebTerminal
      ) {
        payment_method = PaymentMethod.MPOS;
      } else if (paymentMethod === EpikPaymentMethod.CreditCardViaEpp) {
        if (raiseNowExtraFields.raisenowUseStripe) {
          payment_method = PaymentMethod.STRIPE;
          payment_provider = PaymentProvider.STRIPE;
        } else {
          payment_method = PaymentMethod.CREDIT_CARD;
        }
      }

      paymentConfig.payment_method = payment_method;
      paymentConfig.payment_provider = payment_provider;
    }

    return {
      ...paymentConfig,
      language,
      flow: PaymentFlow.EPP,
    };
  }

  /**
   * @param campaign Campaign config
   *
   * @returns
   * EPIK config from `campaign`.
   */
  private createEpikConfig(campaign: Campaign): EpikConfig {
    const { raisenow_api_key: apiKey, raisenow_test_mode: testMode } =
      campaign.raisenow;

    const language = languageFromLocale(
      this.translationService.currentLanguage,
    );
    const debug = isBoolean(testMode) ? testMode : this.rnwSettings.testMode();

    return {
      language,
      debug,
      eppApiKey: apiKey,
    };
  }

  /**
   * Opens the MPOS terminal webapp for the given `transactionId`.
   *
   * @param transactionId EPP transaction ID
   *
   * @returns
   * An `Observable` that emits with the current transaction info
   * and that completes when the transaction status is no longer 'pending'.
   */
  private openMposTab$(transactionId: string) {
    return defer(() => {
      const mposWindow = this.rnPaymentService.openMposTab(transactionId);

      return this.rnPaymentService.pollTransactionInfo().pipe(
        takeUntil(this._cancelPayment$),
        finalize(() => {
          if (!mposWindow.closed) {
            mposWindow.close();
          }
        }),
      );
    });
  }

  private localizeCreditCardIframe(): RaiseNowCreditCardUpdateTranslationsPayload {
    return prepareAdditionalRaiseNowCreditCardTranslations();
  }
}

/**
 * Creates an `Observable` that emits with the instance of the created
 * Creadit Card Iframe once the iframe was initialized and emitted the
 * 'ready' event, then completes.
 * In case of an iframe error, it will emit an error notification.
 *
 * @param payment Payment to initialize the iframe for
 * @param targetSelector Selector used to place the iframe
 *
 * @returns
 * `Observable` that emits the credit card iframe instnace.
 */
function showCreditCardIframe$(
  payment: Payment,
  targetSelector?: string,
): Observable<CreditCardIframe> {
  return defer(() => from(payment.showCreditCardIframe(targetSelector))).pipe(
    concatMap((ccIframe) =>
      race(
        fromCreditCardIframeMessage(ccIframe, 'rnw.credit-card.ready'),
        fromEvent(ccIframe.iframe, 'error').pipe(
          switchMap((e) => throwError(e)),
        ),
      ).pipe(map(() => ccIframe)),
    ),
    first(),
  );
}
